import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useUpsertQRMutation } from "modules/campaigns/hooks/sections/qr/useUpsertQRMutation"
import { Loading } from "modules/shared/components/Loading"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { QRResponseDataSchema, UpsertQRSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"

type UpsertQRModalProps = {
  onClose: () => void
  data?: z.infer<typeof QRResponseDataSchema>
  isPending?: boolean
}

export const UpsertQRModal = ({ onClose, data, isPending = false }: UpsertQRModalProps) => {
  const { t } = useTranslation()
  const { mutateAsync } = useUpsertQRMutation()
  const form = useForm<z.infer<typeof UpsertQRSchema>>({
    resolver: zodResolver(UpsertQRSchema),
    defaultValues: {
      name: "",
    },
  })

  const onSubmit = async (payload: z.infer<typeof UpsertQRSchema>) => {
    await mutateAsync({ name: payload.name, id: data?.id })
    toast.success(t("campaigns.templates.qr.success.update"))
    onClose()
  }

  useEffect(() => {
    if (data) {
      form.reset(data)
    }
  }, [data, form])

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="flex flex-col gap-8">
        {isPending ? (
          <div className="py-4 flex items-center justify-center">
            <Loading />
          </div>
        ) : (
          <>
            <DialogTitle>
              {data
                ? t("campaigns.templates.qr.modal.edit")
                : t("campaigns.templates.qr.modal.create")}
            </DialogTitle>
            <Form
              {...form}
              errorTranslations={getErrorTranslations()}
              onSubmit={onSubmit ? form.handleSubmit(onSubmit) : undefined}
              className="flex flex-col gap-4"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-6 lg:col-span-4">
                    <FormLabel>{t("campaigns.templates.qr.modal.identifier.label")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        required
                        placeholder={t("campaigns.templates.qr.modal.identifier.placeholder")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Form>
          </>
        )}
        <DialogFooter>
          <div className="flex justify-between w-full">
            <Button variant={"outline"} onClick={onClose}>
              {t("campaigns.templates.qr.modal.cancel")}
            </Button>
            <Button onClick={form.handleSubmit(onSubmit)}>
              {t("campaigns.templates.qr.modal.save")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
